import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PenNib, Eye, EyeSlash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { user, login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name || "Auteur");
      toast.success("Bienvenue dans EDITORIX");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-[46%] bg-foreground text-background flex-col justify-between p-14 relative overflow-hidden">
        <div className="flex items-center gap-3">
          <PenNib size={30} weight="duotone" className="text-primary" />
          <span className="font-serif text-2xl tracking-tight">EDITORIX</span>
        </div>
        <div className="relative z-10">
          <p className="overline text-primary mb-6">Un projet GABRYX</p>
          <h1 className="font-serif text-5xl xl:text-6xl leading-[1.05] font-light mb-6">
            Votre éditeur<br />littéraire personnel.
          </h1>
          <p className="text-background/70 max-w-md text-base leading-relaxed">
            Importez un manuscrit complet. EDITORIX détecte vos chapitres, analyse chaque page et
            construit la mémoire de votre roman. <span className="italic font-serif text-lg">EDITORIX suggère, l'auteur décide.</span>
          </p>
        </div>
        <p className="text-background/40 text-xs font-mono">Assistance éditoriale · v2</p>
        <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-primary/20 blur-3xl" />
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <PenNib size={26} weight="duotone" className="text-primary" />
            <span className="font-serif text-xl">EDITORIX</span>
          </div>
          <h2 className="font-serif text-3xl mb-1">{mode === "login" ? "Connexion" : "Créer un compte"}</h2>
          <p className="text-muted-foreground text-sm mb-8">
            {mode === "login" ? "Reprenez votre travail éditorial." : "Commencez votre premier manuscrit."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-xs">Nom</Label>
                <Input id="name" data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom" className="mt-1" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs">Email</Label>
              <Input id="email" data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@exemple.com" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs">Mot de passe</Label>
              <div className="relative mt-1">
                <Input id="password" data-testid="auth-password-input" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button type="submit" data-testid="auth-submit-button" disabled={loading} className="w-full rounded-sm h-11 mt-2">
              {loading ? "…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <span className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground uppercase tracking-wide">ou</span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <button
            type="button"
            data-testid="google-signin-button"
            onClick={loginWithGoogle}
            className="w-full h-11 rounded-sm border border-border bg-surface hover:bg-surface-hover transition-colors flex items-center justify-center gap-3 font-medium"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
              <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001 6.19 5.238 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
            </svg>
            Continuer avec Google
          </button>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {mode === "login" ? "Pas encore de compte ?" : "Déjà inscrit ?"}{" "}
            <button
              data-testid="auth-toggle-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-primary font-medium hover:underline"
            >
              {mode === "login" ? "Créer un compte" : "Se connecter"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
